"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import ArrowIosDown from "@crehana/compass.icons.outline/ArrowIosDown";
import ArrowIosUp from "@crehana/compass.icons.outline/ArrowIosUp";
import Checkmark from "@crehana/compass.icons.outline/Checkmark";
import { SimpleTooltip } from "../Tooltip/index.js";
import { cn } from "../utils.js";
import * as __rspack_external_react from "react";
import * as __rspack_external__radix_ui_react_select_4606f4d3 from "@radix-ui/react-select";
const Select = __rspack_external__radix_ui_react_select_4606f4d3.Root;
const SelectGroup = __rspack_external__radix_ui_react_select_4606f4d3.Group;
const SelectValue = __rspack_external__radix_ui_react_select_4606f4d3.Value;
const SelectTrigger = /*#__PURE__*/ __rspack_external_react.forwardRef(({ className, children, unstyled, renderIcon: renderIconProp, tooltipValue, ...props }, ref)=>{
    const renderIcon = ()=>{
        if (renderIconProp) return renderIconProp();
        return /*#__PURE__*/ jsxs(Fragment, {
            children: [
                /*#__PURE__*/ jsx(ArrowIosDown, {
                    size: 24,
                    className: cn('arrow-ios-down group-data-[state=open]:tw-hidden', props.disabled ? 'tw-text-neutral-light-500 dark:tw-text-neutral-dark-600' : 'tw-text-primary-light-500 tw-shrink-0 dark:tw-text-secondary-dark-500')
                }),
                /*#__PURE__*/ jsx(ArrowIosUp, {
                    size: 24,
                    className: cn('arrow-ios-up group-data-[state=closed]:tw-hidden', props.disabled ? 'tw-text-neutral-light-500 dark:tw-text-neutral-dark-600' : 'tw-text-primary-light-500 tw-shrink-0 dark:tw-text-secondary-dark-500')
                })
            ]
        });
    };
    const icon = renderIcon();
    const trigger = unstyled ? /*#__PURE__*/ jsx(__rspack_external__radix_ui_react_select_4606f4d3.Trigger, {
        ref: ref,
        className: cn('tw-group tw-cursor-pointer tw-justify-between tw-flex tw-items-center', className),
        ...props,
        children: /*#__PURE__*/ jsx("span", {
            className: "tw-line-clamp-1 tw-grow tw-text-left",
            children: children
        })
    }) : /*#__PURE__*/ jsxs(__rspack_external__radix_ui_react_select_4606f4d3.Trigger, {
        ref: ref,
        className: cn('tw-group tw-cursor-pointer tw-justify-between tw-input tw-flex tw-items-center', 'enabled:data-[state=closed]:tw-border-neutral-light-600 enabled:data-[state=closed]:tw-ring-neutral-light-600', className),
        ...props,
        children: [
            /*#__PURE__*/ jsx("span", {
                className: "tw-line-clamp-1 tw-grow tw-text-left",
                children: children
            }),
            icon ? /*#__PURE__*/ jsx(__rspack_external__radix_ui_react_select_4606f4d3.Icon, {
                children: /*#__PURE__*/ jsx("div", {
                    className: "tw-flex tw-items-center tw-shrink-0",
                    children: icon
                })
            }) : null
        ]
    });
    if (tooltipValue) return /*#__PURE__*/ jsx(SimpleTooltip, {
        value: tooltipValue,
        className: "tw-max-w-[400px]",
        side: "top",
        delayDuration: 150,
        children: trigger
    });
    return trigger;
});
SelectTrigger.displayName = __rspack_external__radix_ui_react_select_4606f4d3.Trigger.displayName;
const SelectContent = /*#__PURE__*/ __rspack_external_react.forwardRef(({ className, children, position = 'popper', removePortal, multiline, ...props }, ref)=>{
    const Wrapper = removePortal ? __rspack_external_react.Fragment : __rspack_external__radix_ui_react_select_4606f4d3.Portal;
    return /*#__PURE__*/ jsx(Wrapper, {
        children: /*#__PURE__*/ jsx(__rspack_external__radix_ui_react_select_4606f4d3.Content, {
            ref: ref,
            className: cn('tw-max-h-24 tw-z-popover tw-overflow-auto tw-w-full tw-rounded', 'tw-bg-neutral-light-100 dark:tw-bg-neutral-dark-700 dark:tw-shadow-none', 'tw-relative tw-z-50 rounded-md border border-neutral-200 bg-white text-neutral-950 tw-shadow-03', 'data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95', 'data-[side=bottom]:tw-slide-in-from-top-2 data-[side=left]:tw-slide-in-from-right-2 data-[side=right]:tw-slide-in-from-left-2 data-[side=top]:tw-slide-in-from-bottom-2', 'popper' === position && 'data-[side=bottom]:tw-translate-y-1 data-[side=left]:-tw-translate-x-1 data-[side=right]:tw-translate-x-1 data-[side=top]:-tw-translate-y-1', className),
            position: position,
            ...props,
            children: /*#__PURE__*/ jsx(__rspack_external__radix_ui_react_select_4606f4d3.Viewport, {
                className: cn('', 'popper' === position && 'h-[var(--radix-select-trigger-height)] tw-w-full tw-min-w-[var(--radix-select-trigger-width)]', multiline && 'tw-max-w-[500px]'),
                style: {
                    overflow: void 0
                },
                children: children
            })
        })
    });
});
SelectContent.displayName = __rspack_external__radix_ui_react_select_4606f4d3.Content.displayName;
const SelectLabel = /*#__PURE__*/ __rspack_external_react.forwardRef(({ className, ...props }, ref)=>/*#__PURE__*/ jsx(__rspack_external__radix_ui_react_select_4606f4d3.Label, {
        ref: ref,
        className: cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className),
        ...props
    }));
SelectLabel.displayName = __rspack_external__radix_ui_react_select_4606f4d3.Label.displayName;
const MULTILINE_CHAR_THRESHOLD = 150;
const SelectItem = /*#__PURE__*/ __rspack_external_react.forwardRef(({ className, children, removeCheck, multiline, ...props }, ref)=>{
    const isMultiline = multiline ?? ('string' == typeof children && children.length > MULTILINE_CHAR_THRESHOLD);
    return /*#__PURE__*/ jsxs(__rspack_external__radix_ui_react_select_4606f4d3.Item, {
        ref: ref,
        className: cn('tw-group tw-relative tw-flex tw-gap-1.2 tw-w-full tw-justify-between tw-cursor-pointer tw-select-none tw-rounded-sm tw-outline-none', 'tw-font-body4 tw-py-1.2 tw-px-2.4', isMultiline ? 'tw-min-h-4.8 tw-items-start tw-overflow-hidden' : 'tw-h-4.8 tw-items-center', 'data-[disabled]:tw-pointer-events-none data-[disabled]:tw-opacity-50', 'hover:tw-bg-neutral-light-300 dark:hover:tw-bg-neutral-dark-600 hover:tw-text-primary-light-500', 'data-[state=checked]:tw-text-primary-light-500 dark:data-[state=checked]:tw-bg-neutral-dark-600 data-[state=checked]:tw-bg-neutral-light-300 dark:data-[state=checked]:bg-neutral-dark-600', 'dark:tw-text-neutral-light-100', className),
        ...props,
        children: [
            isMultiline ? /*#__PURE__*/ jsx("span", {
                className: "tw-min-w-0 tw-break-words",
                children: /*#__PURE__*/ jsx(__rspack_external__radix_ui_react_select_4606f4d3.ItemText, {
                    children: children
                })
            }) : /*#__PURE__*/ jsx(__rspack_external__radix_ui_react_select_4606f4d3.ItemText, {
                children: children
            }),
            !removeCheck && /*#__PURE__*/ jsx(__rspack_external__radix_ui_react_select_4606f4d3.ItemIndicator, {
                children: /*#__PURE__*/ jsx(Checkmark, {
                    size: 24,
                    className: "tw-text-success-300"
                })
            })
        ]
    });
});
SelectItem.displayName = __rspack_external__radix_ui_react_select_4606f4d3.Item.displayName;
const SelectSeparator = /*#__PURE__*/ __rspack_external_react.forwardRef(({ className, ...props }, ref)=>/*#__PURE__*/ jsx(__rspack_external__radix_ui_react_select_4606f4d3.Separator, {
        ref: ref,
        className: cn('-mx-1 my-1 h-px bg-neutral-100 dark:bg-neutral-800', className),
        ...props
    }));
SelectSeparator.displayName = __rspack_external__radix_ui_react_select_4606f4d3.Separator.displayName;
export { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue };
